import { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { Search } from "lucide-react"

import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"

/**
 * The search palette. The header owns `open` so a second trigger (the phone
 * header's icon) can open the same dialog; uncontrolled use still works.
 */
export function SearchModal({
  open: openProp,
  onOpenChange,
}: {
  open?: boolean
  onOpenChange?: (open: boolean) => void
} = {}) {
  const [ownOpen, setOwnOpen] = useState(false)
  const open = openProp ?? ownOpen
  const setOpen = useCallback(
    (next: boolean) => (onOpenChange ? onOpenChange(next) : setOwnOpen(next)),
    [onOpenChange],
  )
  const [query, setQuery] = useState("")
  const navigate = useNavigate()

  // Cmd+K / Ctrl+K keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        // The article editor claims Cmd+K for link editing — ProseMirror
        // preventDefaults shortcuts it handles, and this document-level
        // listener runs after the editor's, so defaultPrevented is exactly
        // "someone closer already took this keystroke". Without the check
        // both the search palette and the link popover opened stacked.
        if (e.defaultPrevented) return
        e.preventDefault()
        setOpen(true)
      }
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [setOpen])

  const handleSubmit = useCallback(() => {
    const trimmed = query.trim()
    if (!trimmed) return
    setOpen(false)
    navigate(`/search?q=${encodeURIComponent(trimmed)}`)
    setQuery("")
  }, [query, navigate, setOpen])

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="hidden md:flex items-center gap-2 w-72 lg:w-96 h-9 rounded-md border border-input bg-background px-3 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
      >
        <Search className="h-4 w-4 shrink-0" />
        <span className="flex-1 text-left">Search...</span>
        <kbd className="hidden sm:inline-flex h-5 items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
          <span className="text-xs">{navigator.platform?.includes("Mac") ? "\u2318" : "Ctrl"}</span>K
        </kbd>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className={cn(
          "top-[20%] translate-y-0 sm:max-w-xl p-0 gap-0 overflow-hidden",
          "data-[state=open]:slide-in-from-top-[18%] data-[state=closed]:slide-out-to-top-[18%]",
        )}>
          <DialogTitle className="sr-only">Search content</DialogTitle>
          <div className="flex items-center border-b px-4">
            <Search className="h-5 w-5 text-muted-foreground shrink-0" />
            <Input
              placeholder="Search your content... (e.g. &quot;articles about taxes&quot;)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              className="h-14 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-base placeholder:text-muted-foreground/60"
              autoFocus
            />
          </div>
          <div className="p-4 text-center text-sm text-muted-foreground">
            <p>
              Uses AI embeddings to find semantically related content across WordPress posts, articles, news, and topics.
            </p>
            <p className="mt-2 text-xs">
              Press <kbd className="rounded border bg-muted px-1 py-0.5 font-mono text-[10px]">Enter</kbd> to search
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
